import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { AvaHiveOrb } from "@/components/ui/AvaHiveOrb";
import { HiveProvider } from "@/context/HiveContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Coach from "@/pages/Coach";
import Workouts from "@/pages/Workouts";
import ProgressPage from "@/pages/Progress";
import Schedule from "@/pages/Schedule";
import Membership from "@/pages/Membership";
import PaymentResult from "@/pages/PaymentResult";
import Clients from "@/pages/Clients";
import CRM from "@/pages/CRM";
import Leaderboard from "@/pages/Leaderboard";
import Feed from "@/pages/Feed";
import Nutrition from "@/pages/Nutrition";
import MindFlow from "@/pages/MindFlow";
import Payments from "@/pages/Payments";
import Train from "@/pages/Train";
import HelpCentre from "@/pages/HelpCentre";
import Padel from "@/pages/Padel";
import AvaSso from "@/pages/AvaSso";
import Legal from "@/pages/Legal";
import Diary from "@/pages/Diary";
import WarmUp from "@/pages/WarmUp";
import MyClub from "@/pages/MyClub";
import Areas, { AreaDetail } from "@/pages/Areas";
import Today from "@/pages/Today";
import Programming from "@/pages/Programming";
import CalendarPage from "@/pages/CalendarPage";
import Benchmarks from "@/pages/Benchmarks";
import Performance from "@/pages/Performance";
import Timer from "@/pages/Timer";
import Community from "@/pages/Community";
import Messages from "@/pages/Messages";
import GymTV from "@/pages/GymTV";
import Competitions from "@/pages/Competitions";
import Habits from "@/pages/Habits";
import TimetablePage from "@/pages/TimetablePage";
import { useParams } from "react-router-dom";

import { FEATURES } from "@/lib/features";
import { useLocation } from "react-router-dom";

function JoinRedirect() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  if (loading) return null;
  try { localStorage.setItem("octon_club_code", (code || "").toUpperCase()); } catch (_) {}
  return <Navigate to={user ? `/app/membership?club=${(code || "").toUpperCase()}` : "/login?mode=register"} replace />;
}

function Loading() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <AvaHiveOrb status="connecting" size={160} />
      <p className="mono-label">loading…</p>
    </div>
  );
}

// Members (clients) need an active plan once the free taster has ended.
const PLAN_FREE_PATHS = ["/app/membership", "/app/payments"];
function needsPlan(user) {
  if (!user || user.role !== "client") return false;
  if (user.membership_status === "active" || user.membership_status === "past_due") return false;
  if (user.club_active) return false;
  if (user.membership_status === "trialing" && user.trial_ends_at && new Date(user.trial_ends_at) > new Date()) return false;
  return true;
}

function SignedIn({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Protected({ children, trainerOnly, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (trainerOnly && !(user.role === "trainer" || user.role === "admin")) return <Navigate to="/app" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  if (needsPlan(user) && !PLAN_FREE_PATHS.includes(location.pathname)) return <Navigate to="/app/membership" replace />;
  return <AppShell>{children}</AppShell>;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/ava-sso" element={<AvaSso />} />
      <Route path="/help" element={<HelpCentre />} />
      <Route path="/join/:code" element={<JoinRedirect />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/refunds" element={<Legal />} />
      <Route path="/contact" element={<Legal />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/coach" element={<Protected><Coach /></Protected>} />
      <Route path="/app/train" element={<Protected><Train /></Protected>} />
      <Route path="/app/workouts" element={<Protected><Workouts /></Protected>} />
      <Route path="/app/diary" element={<Protected><Diary /></Protected>} />
      <Route path="/app/warmup" element={<Protected><WarmUp /></Protected>} />
      <Route path="/app/club" element={<Protected><MyClub /></Protected>} />
      <Route path="/app/areas" element={<Protected><Areas /></Protected>} />
      <Route path="/app/areas/:key" element={<Protected><AreaDetail /></Protected>} />
      <Route path="/app/padel" element={FEATURES.padel ? <Protected><Padel /></Protected> : <Navigate to="/app/train" replace />} />
      <Route path="/app/progress" element={<Protected><ProgressPage /></Protected>} />
      <Route path="/app/today" element={<Protected><Today /></Protected>} />
      <Route path="/app/programming" element={<Protected><Programming /></Protected>} />
      <Route path="/app/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/app/benchmarks" element={<Protected><Benchmarks /></Protected>} />
      <Route path="/app/performance" element={<Protected><Performance /></Protected>} />
      <Route path="/app/timer" element={<Protected><Timer /></Protected>} />
      <Route path="/app/community" element={<Protected><Community /></Protected>} />
      <Route path="/app/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/app/competitions" element={<Protected><Competitions /></Protected>} />
      <Route path="/app/habits" element={<Protected><Habits /></Protected>} />
      <Route path="/app/timetable" element={<Protected><TimetablePage /></Protected>} />
      <Route path="/tv" element={<SignedIn><GymTV /></SignedIn>} />
      <Route path="/app/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/app/feed" element={<Protected><Feed /></Protected>} />
      <Route path="/app/schedule" element={<Protected><Schedule /></Protected>} />
      <Route path="/app/membership" element={<Protected><Membership /></Protected>} />
      <Route path="/app/nutrition" element={<Protected><Nutrition /></Protected>} />
      <Route path="/app/mind" element={<Protected><MindFlow /></Protected>} />
      <Route path="/app/payments" element={<Protected><Payments /></Protected>} />
      <Route path="/app/clients" element={<Protected trainerOnly><Clients /></Protected>} />
      <Route path="/app/crm" element={<Protected adminOnly><CRM /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <HiveProvider>
            <AppRouter />
            <Toaster position="top-right" theme="dark" />
          </HiveProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
